import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import Button from '../../atoms/Button/Button'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { SiteSchema } from '@toasttab/shared/src/interfaces/Site.d'
import _helpers from '@toasttab/shared/src/globals/_helpers'

export interface RegionModalProps {
  packageName: string
  regionData: {
    countryCode: string
    regionPreference: string
  }
}

const getRegion = (region: string | undefined) => {
  if (!region) {
    return 'US'
  } else {
    // Check if the region is 'UK' and return 'GB' if it matches
    if (region.toUpperCase() === 'UK') {
      return 'GB'
    }
    // Check if the region matches any site and return the region in uppercase
    return Sites.sites.find((site) => site.craftSite === region.toUpperCase())
      ? region.toUpperCase()
      : 'US'
  }
}

const RegionModal = ({ packageName, regionData }: RegionModalProps) => {
  const [isOpen, setIsOpen] = useState(!regionData.regionPreference)
  const [regionSelection, setRegionSelection] = useState('')
  useEffect(() => {
    if (regionData && regionData.countryCode) {
      Sites.sites
        .filter((site) => site.defaultRegion && site.regionCode !== 'au')
        .forEach((site: SiteSchema) => {
          if (
            site.countryCode.toString().toUpperCase() ===
            regionData.countryCode.toUpperCase()
          ) {
            setRegionSelection(site.url)
          }
        })
    }
  }, [])

  const handleAfterSubmitting = () => {
    const submitedRegion = regionSelection || 'US'
    const parseRegion: Array<string> = submitedRegion.split(/[\/\/]/)
    const region: string = getRegion(parseRegion[1])
    if (region) {
      const regionTarget =
        packageName === 'onTheLine'
          ? `${regionSelection}/blog/on-the-line`
          : regionSelection
      _helpers.createCookie(`_preferredRegion`, `${region}`, 30, '/')
      window.location.assign(regionTarget)
    }
  }

  const handleClose = () => {
    _helpers.createCookie(`_preferredRegion`, `US`, 30, '/')
    setIsOpen(false)
  }

  const handleCountryChange = (e: any) => {
    const selectedRegion = e.target.value
    setRegionSelection(selectedRegion)
  }

  const selectCssClass = classNames({
    select: true,
    'block w-full  px-[13.6px] py-[16px] border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500':
      true
  })
  return (
    isOpen && (
      <div
        className='modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50'
        data-testid='RegionModal'
      >
        <div className='bg-white rounded-lg shadow-lg p-6 w-[300px] min-w-[300px] md:w-[552px] md:min-w-[552px] relative'>
          <button
            onClick={handleClose}
            className='absolute top-2 right-2 text-gray-500 hover:text-gray-700'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-6 w-6'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
          <div className='modal__card-content text-center p-4 md:p-6 lg:p-10 '>
            <h4 className='font-effra mb-4 font-medium'>
              Choose a country or region to see content specific to your
              location.
            </h4>
            <div className='mb-4'>
              <select
                value={regionSelection}
                onChange={handleCountryChange}
                className={selectCssClass}
              >
                {Sites.sites
                  .filter(
                    (site) => site.defaultRegion && site.regionCode !== 'au'
                  )
                  .map((site: SiteSchema, i: number) => {
                    return (
                      <option
                        key={`regionItem-${i}`}
                        data-countrycode={site.countryCode}
                        value={site.url}
                      >
                        {site.siteName}
                      </option>
                    )
                  })}
              </select>
            </div>
            <Button
              variation='primary'
              size='medium'
              fontWeight={['font-semibold']}
              classes='mt-5'
              onClick={() => handleAfterSubmitting()}
              heapInteraction='regionModal_primarybtn_click'
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    )
  )
}

export default RegionModal
